import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash2, Shield, PenTool } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Benutzer angelegt.");
      setForm({ name: "", email: "", password: "", role: "editor" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Benutzer wirklich löschen?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Gelöscht."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler."); }
  };

  const field = "w-full border border-edborder bg-white px-4 py-2.5 text-sm outline-none focus:border-prussian";
  const lbl = "block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1.5 font-semibold";

  return (
    <AdminLayout title="Benutzer">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-edborder overflow-hidden" data-testid="admin-users-list">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-prussian text-white text-left uppercase text-[11px] tracking-wide">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">E-Mail</th>
                <th className="px-4 py-3">Rolle</th>
                <th className="px-4 py-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edborder">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-offwhite">
                  <td className="px-4 py-3 font-semibold text-prussian">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 uppercase tracking-wide ${u.role === "admin" ? "bg-prussian text-white" : "bg-gold/20 text-gold-hover"}`}>
                      {u.role === "admin" ? <Shield size={11} /> : <PenTool size={11} />}
                      {u.role === "admin" ? "Administrator" : "Redakteur"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== user.id ? (
                      <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="w-8 h-8 inline-flex items-center justify-center text-breaking border border-edborder hover:bg-breaking/10"><Trash2 size={14} /></button>
                    ) : <span className="text-[11px] text-muted-foreground">Sie</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-edborder p-6 h-fit">
          <h2 className="font-headline font-bold text-xl text-prussian mb-1 flex items-center gap-2"><UserPlus size={18} /> Neuer Benutzer</h2>
          <p className="text-xs text-muted-foreground font-body mb-5">Redakteure verwalten nur eigene Artikel.</p>
          <form onSubmit={create} data-testid="create-user-form" className="space-y-4">
            <div><label className={lbl}>Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="user-name-input" /></div>
            <div><label className={lbl}>E-Mail</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="user-email-input" /></div>
            <div><label className={lbl}>Passwort</label><input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} data-testid="user-password-input" /></div>
            <div><label className={lbl}>Rolle</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={field} data-testid="user-role-select">
                <option value="editor">Redakteur</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <button type="submit" disabled={saving} data-testid="create-user-btn" className="w-full bg-prussian text-white font-body font-semibold text-sm py-2.5 hover:bg-prussian-dark disabled:opacity-50">
              {saving ? "Anlegen…" : "Benutzer anlegen"}
            </button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
